import type { StrictPaddingStyle } from "../util/types";

import { useTheme } from "../ThemeProvider";
import { useAnimatedNative } from "../util/useAnimatedNative";

import React, { useEffect, useRef } from "react";
import { View, StyleSheet, Animated, Easing } from "react-native";
import Svg, { Circle } from "react-native-svg";

export interface SpinnerProps {
  /**
   * Whether to use inverse colors for the spinner.
   */
  inverse?: boolean;

  /**
   * Aria label for the spinner.
   */
  ariaLabel?: string;

  /**
   * Defines the padding style overrides.
   */
  sx?: StrictPaddingStyle;

  /**
   * Add the accessibilityLabel
   */
  accessibilityLabel?: string;
}

export function Spinner({
  inverse = false,
  sx,
  ariaLabel,
  accessibilityLabel = "Loading",
}: SpinnerProps) {
  const theme = useTheme();
  const size = 24;
  const strokeWidth = 3;
  const radius = size / 2 - strokeWidth / 2;
  const center = size / 2;
  const circumference = 2 * Math.PI * radius;

  const backgroundColor = inverse ? theme.colors.spinnerInverse : theme.colors.spinner;
  const highlightColor = inverse ? theme.colors.background : theme.colors.backgroundPrimary;

  const AnimatedCircle = Animated.createAnimatedComponent(Circle);
  const progress = useRef(new Animated.Value(0.25)).current;
  const rotation = useRef(new Animated.Value(0)).current;
  const dashMinimum = 0.3;
  const dashMaximum = 0.7;

  const useNativeDriver = useAnimatedNative();

  useEffect(() => {
    const progressAnimation = Animated.sequence([
      Animated.timing(progress, {
        toValue: dashMaximum,
        duration: 700,
        easing: Easing.inOut(Easing.quad),
        useNativeDriver: useNativeDriver,
      }),
      Animated.timing(progress, {
        toValue: dashMinimum,
        duration: 700,
        easing: Easing.inOut(Easing.quad),
        useNativeDriver: useNativeDriver,
      }),
    ]);

    const progressLoop = Animated.loop(progressAnimation);
    const rotationLoop = Animated.loop(
      Animated.timing(rotation, {
        toValue: 360,
        duration: 500,
        easing: Easing.linear,
        useNativeDriver: useNativeDriver,
      }),
    );

    progressLoop.start();
    rotationLoop.start();

    return () => {
      progressLoop.stop();
      rotationLoop.stop();
    };
  }, [progress, rotation, useNativeDriver]);

  const strokeDashoffset = progress.interpolate({
    inputRange: [dashMinimum, dashMaximum],
    outputRange: [circumference * 0.3, circumference * 0.7],
  });

  const rotateInterpolate = rotation.interpolate({
    inputRange: [0, 360],
    outputRange: ["0deg", "360deg"],
  });

  return (
    <View
      style={[styles.container, sx]}
      aria-label={ariaLabel}
      accessible={true}
      accessibilityRole="image"
      accessibilityLabel={accessibilityLabel}
    >
      <Animated.View style={[{ transform: [{ rotate: rotateInterpolate }] }]}>
        <Svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} testID="spinner">
          <Circle
            testID="spinner-circle"
            cx={center}
            cy={center}
            r={radius}
            stroke={backgroundColor}
            strokeWidth={strokeWidth}
            fill="none"
          />
          <AnimatedCircle
            testID="spinner-animated-circle"
            cx={center}
            cy={center}
            r={radius}
            stroke={highlightColor}
            fill="none"
            strokeWidth={strokeWidth}
            strokeLinecap={"round"}
            strokeDasharray={`${circumference}, ${circumference}`}
            strokeDashoffset={strokeDashoffset}
          />
        </Svg>
      </Animated.View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    alignItems: "center",
    justifyContent: "center",
  },
});
